import React, { useState } from 'react';
import axios from 'axios';

import { useStyles } from '~/assets/styles';
import { ConfirmModal } from '~/components/ConfirmModal';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import type { OrganizationModel } from '~/components/types/organization-types';
import { reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import { useSysconfig } from '../SystemConfigurationScreen';

import AiClaimImportCard from './AiClaimImport/AiClaimImportCard';
import { useAiClaimImport } from './AiClaimImport/hooks/useAiClaimImport';
import ImportByClaimsDialog from './AiClaimImport/ImportByClaimsDialog';
import ImportBySubOrgDialog from './AiClaimImport/ImportBySubOrgDialog';
import { useSopConfiguration } from './SopConfiguration/hooks/useSopConfiguration';
import OrgSopViewDialog from './SopConfiguration/OrgSopViewDialog';
import SopCard from './SopConfiguration/SopCard';
import SopConfigurationDialog from './SopConfiguration/SopConfigurationDialog';

interface CliveAccountConfig {
  account_id?: string;
}

interface AccountProps {
  account?: CliveAccountConfig;
  isLoading: boolean;
  isError: boolean;
  reloadAccount: () => Promise<CliveAccountConfig | undefined>;
}

const Account: React.FC<AccountProps> = ({ account, isLoading, isError, reloadAccount }) => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSopDialogOpen, setIsSopDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false);
  const [isImportBySubOrgDialogOpen, setIsImportBySubOrgDialogOpen] = useState(false);
  const [isImportByClaimsDialogOpen, setIsImportByClaimsDialogOpen] = useState(false);
  const baseConfigsRoute = `/api/v1/organizations/${organization?.id}/ai_configuration`;
  const { classes } = useStyles();

  const hasCliveAccount = !!account?.account_id;

  const {
    configuration: sopConfiguration,
    isLoading: isSopLoading,
    reloadData: reloadSopConfig,
  } = useSopConfiguration({ enabled: hasCliveAccount });

  const { runList, isRunListLoading, reloadRunList, fetchRunProgress } = useAiClaimImport();

  const handleConfigureClick = () => {
    if (sopConfiguration) {
      setIsReplaceConfirmOpen(true);
    } else {
      setIsSopDialogOpen(true);
    }
  };

  const handleReplaceConfirm = () => {
    setIsReplaceConfirmOpen(false);
    setIsSopDialogOpen(true);
  };

  const handleCreateAccount = async (): Promise<void> => {
    try {
      setIsSubmitting(true);
      await axios.post(`${baseConfigsRoute}/create_account`);
      await reloadAccount();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-[50vh]">
        <LoadingSwitch isLoading={isLoading} isError={isError}>
          <div className="h-full w-full">
            {/* Clive Account Section */}
            <div>
              {hasCliveAccount ? null : (
                <Button
                  variant="contained"
                  className={cn(classes.cancelButton)}
                  onClick={handleCreateAccount}
                  disabled={isSubmitting}
                >
                  Create Clive Account
                </Button>
              )}
            </div>

            {/* SOP Configuration Section - Only shown when Clive account exists */}
            {hasCliveAccount && (
              <div>
                <SopCard
                  configuration={sopConfiguration ?? null}
                  isLoading={isSopLoading}
                  onViewClick={() => setIsViewDialogOpen(true)}
                  onConfigureClick={handleConfigureClick}
                />
              </div>
            )}

            {/* AI Claim Import Section - Only shown when Clive account exists */}
            {hasCliveAccount && (
              <div className="mt-16">
                <AiClaimImportCard
                  subOrganizationsEnabled={!!organization.sub_organizations_enabled}
                  onImportBySubOrgClick={() => setIsImportBySubOrgDialogOpen(true)}
                  onImportByClaimsClick={() => setIsImportByClaimsDialogOpen(true)}
                  runList={runList}
                  isRunListLoading={isRunListLoading}
                  fetchRunProgress={fetchRunProgress}
                />
              </div>
            )}
          </div>
        </LoadingSwitch>
      </div>

      {/* SOP Configuration Dialog - Only rendered when Clive account exists */}
      {hasCliveAccount && (
        <SopConfigurationDialog
          open={isSopDialogOpen}
          onClose={() => setIsSopDialogOpen(false)}
          onSaveSuccess={reloadSopConfig}
          isReplacing={!!sopConfiguration}
        />
      )}

      {/* SOP View Dialog - Only rendered when Clive account exists and configuration is loaded */}
      {hasCliveAccount && sopConfiguration && (
        <OrgSopViewDialog
          open={isViewDialogOpen}
          onClose={() => setIsViewDialogOpen(false)}
          configuration={sopConfiguration}
          organizationName={organization.name}
        />
      )}
      {/* AI Claim Import by Sub-Org Dialog */}
      {hasCliveAccount && organization.sub_organizations_enabled && (
        <ImportBySubOrgDialog
          open={isImportBySubOrgDialogOpen}
          onClose={() => setIsImportBySubOrgDialogOpen(false)}
          onImportStarted={reloadRunList}
        />
      )}
      {/* AI Claim Import by Claims Dialog */}
      {hasCliveAccount && (
        <ImportByClaimsDialog
          open={isImportByClaimsDialogOpen}
          onClose={() => setIsImportByClaimsDialogOpen(false)}
          onImportStarted={reloadRunList}
        />
      )}

      {/* Replace SOP Confirmation Dialog */}
      <ConfirmModal
        isOpen={isReplaceConfirmOpen}
        onClose={() => setIsReplaceConfirmOpen(false)}
        title="Replace SOP"
        contentText="Replacing the existing SOP will permanently remove the current files. To keep any part of it, make sure to re-upload the relevant files in your new SOP."
        primaryButtonName="Continue"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleReplaceConfirm}
      />
    </>
  );
};
export default Account;
